import axios from 'axios';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nativeAwareStorage } from './storage';

const initialState = {
  teamExpensesById: {}, // { [teamContactId]: Expense[] }
  businessExpensesById: {}, // { [businessId]: Expense[] }
  categoriesByBusinessId: {}, // { [businessId]: Category[] }
  dashboardSummaryByKey: {}, // { [key]: { summary, categorySummary } }
  businessCurrencyById: {}, // { [businessId]: symbol }
  featureFlagsByTeamId: {}, // { [teamContactId]: FeatureFlag[] }
  featureFlagsErrorByTeamId: {}, // { [teamContactId]: string | null }

  loading: {
    teamExpenses: {},
    businessExpenses: {},
    categories: {},
    dashboard: {},
    currency: {},
    featureFlags: {},
  },
  lastLoadedAt: {
    teamExpenses: {},
    businessExpenses: {},
    categories: {},
    dashboard: {},
    featureFlags: {},
  },
};

export const useExpenseStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // Selectors
      getTeamExpenses: (teamContactId) => get().teamExpensesById[String(teamContactId)] || [],
      getBusinessExpenses: (businessId) => get().businessExpensesById[String(businessId)] || [],
      getCategories: (businessId) => get().categoriesByBusinessId[String(businessId)] || [],
      getDashboard: (key) => get().dashboardSummaryByKey[String(key)] || { summary: [], categorySummary: [] },
      getBusinessCurrency: (businessId) => get().businessCurrencyById[String(businessId)],
      getFeatureFlags: (teamContactId) => get().featureFlagsByTeamId[String(teamContactId)] || [],
      hasFeatureForTeam: (teamContactId, featureName) => {
        const list = get().featureFlagsByTeamId[String(teamContactId)] || [];
        const found = list.find((f) => f.FeatureFlag === featureName);
        return found ? !!found.Granted : false;
      },

      // Loaders (idempotent on same session; skip if already loaded once)
      loadExpensesByTeamContact: async (teamContactId, { force } = {}) => {
        const key = String(teamContactId);
        if (!force && get().lastLoadedAt.teamExpenses[key]) return;
        set((state) => ({ loading: { ...state.loading, teamExpenses: { ...state.loading.teamExpenses, [key]: true } } }));
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpensesListByTeamContactID`,
            { TeamContactID: key }
          );
          const list = Array.isArray(res.data) ? res.data : [];
          set((state) => ({
            teamExpensesById: { ...state.teamExpensesById, [key]: list },
            lastLoadedAt: { ...state.lastLoadedAt, teamExpenses: { ...state.lastLoadedAt.teamExpenses, [key]: Date.now() } },
          }));
        } finally {
          set((state) => ({ loading: { ...state.loading, teamExpenses: { ...state.loading.teamExpenses, [key]: false } } }));
        }
      },

      loadExpensesByBusiness: async (businessId, { force } = {}) => {
        const key = String(businessId);
        if (!force && get().lastLoadedAt.businessExpenses[key]) return;
        set((state) => ({ loading: { ...state.loading, businessExpenses: { ...state.loading.businessExpenses, [key]: true } } }));
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpensesListByBusinessID`,
            { BusinessID: key }
          );
          const list = Array.isArray(res.data) ? res.data : [];
          set((state) => ({
            businessExpensesById: { ...state.businessExpensesById, [key]: list },
            lastLoadedAt: { ...state.lastLoadedAt, businessExpenses: { ...state.lastLoadedAt.businessExpenses, [key]: Date.now() } },
          }));
        } finally {
          set((state) => ({ loading: { ...state.loading, businessExpenses: { ...state.loading.businessExpenses, [key]: false } } }));
        }
      },

      loadCategoriesByBusiness: async (businessId, { force } = {}) => {
        const key = String(businessId);
        if (!force && get().lastLoadedAt.categories[key]) return;
        set((state) => ({ loading: { ...state.loading, categories: { ...state.loading.categories, [key]: true } } }));
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpenseCategoriesByBusiness`,
            { BusinessID: key }
          );
          const list = Array.isArray(res.data) ? res.data : [];
          set((state) => ({
            categoriesByBusinessId: { ...state.categoriesByBusinessId, [key]: list },
            lastLoadedAt: { ...state.lastLoadedAt, categories: { ...state.lastLoadedAt.categories, [key]: Date.now() } },
          }));
        } finally {
          set((state) => ({ loading: { ...state.loading, categories: { ...state.loading.categories, [key]: false } } }));
        }
      },

      loadDashboardSummaries: async ({ BusinessID, TeamContactID, UserRole }, { force } = {}) => {
        console.log('Loading dashboard summaries for', { BusinessID, TeamContactID, UserRole, force });
        const key = `${UserRole}-${BusinessID || ''}-${TeamContactID || ''}`;
        if (!force && get().lastLoadedAt.dashboard[key]) return;
        set((state) => ({ loading: { ...state.loading, dashboard: { ...state.loading.dashboard, [key]: true } } }));
        try {
          // Summary
          const summaryPromise = ['manager', 'owner'].includes(UserRole.toLowerCase())
            ? axios.post(
                `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpensSummaryForAdmin`,
                { BusinessID: String(BusinessID) }
              )
            : axios.post(
                `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetMyExpensSummary`,
                { TeamContactID: String(TeamContactID) }
              );

          // Category Summary
          const categoryPromise = UserRole.toLowerCase() === 'owner'|| UserRole.toLowerCase() === 'manager'
            ? axios.post(
                `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetMyExpensSummaryByCategoryForAdmin`,
                { BusinessID: String(BusinessID) }
              )
            : axios.post(
                `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpenseSummaryByCategoryForTeamContact`,
                { TeamContactID: String(TeamContactID) }
              );

          const [summaryRes, categoryRes] = await Promise.all([summaryPromise, categoryPromise]);
          set((state) => ({
            dashboardSummaryByKey: {
              ...state.dashboardSummaryByKey,
              [key]: {
                summary: Array.isArray(summaryRes.data) ? summaryRes.data : [],
                categorySummary: Array.isArray(categoryRes.data) ? categoryRes.data : [],
              },
            },
            lastLoadedAt: { ...state.lastLoadedAt, dashboard: { ...state.lastLoadedAt.dashboard, [key]: Date.now() } },
          }));
        } finally {
          set((state) => ({ loading: { ...state.loading, dashboard: { ...state.loading.dashboard, [key]: false } } }));
        }
      },

      loadBusinessCurrency: async (businessId) => {
        const key = String(businessId);
        if (get().businessCurrencyById[key]) return;
        set((state) => ({ loading: { ...state.loading, currency: { ...state.loading.currency, [key]: true } } }));
        try {
          const url = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/Onboard/GetBusinessCurrency`;
          const res = await axios.post(url, { BusinessID: key });
          const symbol = res?.data?.Symbol || '₹';
          set((state) => ({ businessCurrencyById: { ...state.businessCurrencyById, [key]: symbol } }));
        } finally {
          set((state) => ({ loading: { ...state.loading, currency: { ...state.loading.currency, [key]: false } } }));
        }
      },

      loadFeatureFlags: async (teamContactId, { force } = {}) => {
        const key = String(teamContactId);
        if (!key) return;
        if (!force && get().lastLoadedAt.featureFlags[key]) return;
        set((state) => ({ loading: { ...state.loading, featureFlags: { ...state.loading.featureFlags, [key]: true } } }));
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/RBAC/GetAppFeaturePermissionByTeam`,
            {
              AppID: process.env.REACT_APP_ID,
              TeamContactID: key,
            },
            { headers: { 'Content-Type': 'application/json', accept: 'application/json' } }
          );
          const list = Array.isArray(res.data) ? res.data : [];
          set((state) => ({
            featureFlagsByTeamId: { ...state.featureFlagsByTeamId, [key]: list },
            featureFlagsErrorByTeamId: { ...state.featureFlagsErrorByTeamId, [key]: null },
            lastLoadedAt: { ...state.lastLoadedAt, featureFlags: { ...state.lastLoadedAt.featureFlags, [key]: Date.now() } },
          }));
        } catch (err) {
          const message = err && err.message ? err.message : 'Failed to load feature flags';
          set((state) => ({ featureFlagsErrorByTeamId: { ...state.featureFlagsErrorByTeamId, [key]: message } }));
        } finally {
          set((state) => ({ loading: { ...state.loading, featureFlags: { ...state.loading.featureFlags, [key]: false } } }));
        }
      },

      // Mutations (refresh or update local store and only fetch as needed)
      addExpense: async (payload) => {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/AddExpense`,
          payload,
          { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
        );
        // Refresh list for that team
        if (payload.TeamContactID) {
          await get().loadExpensesByTeamContact(payload.TeamContactID, { force: true });
        }
        return res.data;
      },

      updateExpense: async (payload) => {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/UpdateExpense`,
          payload
        );
        // Refresh team list after update
        if (payload.TeamContactID) {
          await get().loadExpensesByTeamContact(payload.TeamContactID, { force: true });
        }
        return res.data;
      },

      deleteExpense: async ({ ExpenseID, TeamContactID }) => {
        await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/DeleteExpense`,
          { ExpenseID: String(ExpenseID) }
        );
        const key = String(TeamContactID);
        // Update local cache without a fetch
        const current = get().teamExpensesById[key] || [];
        const next = current.filter((e) => e.ExpenseID !== ExpenseID);
        set((state) => ({ teamExpensesById: { ...state.teamExpensesById, [key]: next } }));
      },

      reviewExpense: async ({ ExpenseID, TeamContactID, Status, Comment, BusinessID }) => {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/ReviewExpense`,
          {
            ExpenseID: String(ExpenseID),
            TeamContactID: String(TeamContactID),
            Status,
            Comment,
          }
        );
        if (BusinessID) {
          await get().loadExpensesByBusiness(BusinessID, { force: true });
        }
        return res.data;
      },

      addCategory: async ({ CategoryName, BusinessID }) => {
        await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/AddExpenseCategory`,
          { CategoryName, BusinessID: String(BusinessID) }
        );
        await get().loadCategoriesByBusiness(BusinessID, { force: true });
      },

      deleteCategory: async ({ CategoryID, BusinessID }) => {
        await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/DeleteExpenseCategory`,
          { CategoryID: String(CategoryID) }
        );
        // Update local cache without a fetch
        const key = String(BusinessID);
        const current = get().categoriesByBusinessId[key] || [];
        const next = current.filter((c) => String(c.ID) !== String(CategoryID));
        set((state) => ({ categoriesByBusinessId: { ...state.categoriesByBusinessId, [key]: next } }));
      },
    }),
    {
      name: 'expense-store',
      storage: createJSONStorage(() => nativeAwareStorage),
      partialize: (state) => ({
        teamExpensesById: state.teamExpensesById,
        businessExpensesById: state.businessExpensesById,
        categoriesByBusinessId: state.categoriesByBusinessId,
        dashboardSummaryByKey: state.dashboardSummaryByKey,
        businessCurrencyById: state.businessCurrencyById,
        lastLoadedAt: state.lastLoadedAt,
      }),
    }
  )
);


